import { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab, TextField, Button, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell,
  Radio,
  Alert, Typography, Box, Chip, CircularProgress, Divider, Tooltip, Avatar,
} from '@mui/material';
import {
  Share as ShareIcon,
  PersonSearch as PersonSearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import professionalAccessService from '../services/professionalAccessService';
import usersService from '../services/usersService';
import invitesService from '../services/invitesService';
import useSnackbar from '../hooks/useSnackbar';
import AppSnackbar from './ui/AppSnackbar';

// Mapear entityType int/string → label
const entityTypeLabels = {
  1: 'Profissional',
  2: 'Agendamentos',
  3: 'Agendas',
  4: 'Clientes',
  Professional: 'Profissional',
  Appointments: 'Agendamentos',
  Schedules: 'Agendas',
  Persons: 'Clientes',
};

// Mapear accessLevel int/string → label
const accessLevelLabels = {
  1: 'Visualização',
  2: 'Edição',
  Read: 'Visualização',
  ReadWrite: 'Edição',
};

// Mapear entityType int/string → key string
const entityTypeToKey = (et) => {
  const map = {
    1: 'professional', 2: 'appointments', 3: 'schedules', 4: 'persons',
    Professional: 'professional', Appointments: 'appointments', Schedules: 'schedules', Persons: 'persons',
  };
  return map[et] || 'unknown';
};

// Converter accessLevel string/int → int (para radio buttons)
const accessLevelToNum = (al) => {
  if (al === 'ReadWrite' || al === 2) return 2;
  if (al === 'Read' || al === 1) return 1;
  return 0;
};

const defaultPermissions = {
  professional: 1,  // Read (obrigatório, fixo)
  appointments: 0,
  schedules: 0,
  persons: 0,
};

const permissionRows = [
  { key: 'professional', entityType: 1, label: '👤 Profissional', disableNone: true },
  { key: 'appointments', entityType: 2, label: '📅 Agendamentos' },
  { key: 'schedules', entityType: 3, label: '📋 Agendas' },
  { key: 'persons', entityType: 4, label: '👥 Clientes' },
];

export default function ShareProfessionalDialog({ open, onClose, professional, onSuccess }) {
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  // Aba ativa
  const [activeTab, setActiveTab] = useState(0);

  // --- Aba Compartilhar ---
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [permissions, setPermissions] = useState({ ...defaultPermissions });
  const [grantLoading, setGrantLoading] = useState(false);

  // --- Aba Gerenciar ---
  const [accessList, setAccessList] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editPermissions, setEditPermissions] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [revokeConfirm, setRevokeConfirm] = useState(null);
  const [revokeLoading, setRevokeLoading] = useState(false);

  // --- Convites ---
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);

  // Reset ao abrir/fechar
  useEffect(() => {
    if (open && professional?.id) {
      loadAccessList();
      loadInvites();
    }
    if (!open) {
      setActiveTab(0);
      setSearchEmail('');
      setSearchResult(null);
      setPermissions({ ...defaultPermissions });
      setEditingUserId(null);
      setRevokeConfirm(null);
    }
  }, [open, professional?.id]);

  // --- Carregar lista de acessos ---
  const loadAccessList = useCallback(async () => {
    if (!professional?.id) return;
    setAccessLoading(true);
    try {
      const data = await professionalAccessService.getAccessList(professional.id);
      setAccessList(data || []);
    } catch {
      showError('Erro ao carregar acessos');
    } finally {
      setAccessLoading(false);
    }
  }, [professional?.id, showError]);

  // --- Carregar convites pendentes ---
  const loadInvites = useCallback(async () => {
    try {
      const data = await invitesService.getAll();
      setPendingInvites((data || []).filter(inv => !inv.acceptedAt));
    } catch {
      // silencioso
    }
  }, []);

  // --- Busca por email ---
  const handleSearch = async () => {
    const email = searchEmail.trim();
    if (!email) return;
    setSearchLoading(true);
    setSearchResult(null);
    try {
      const user = await usersService.lookupByEmail(email);
      setSearchResult({ found: true, user });
    } catch (err) {
      if (err.response?.status === 404) {
        setSearchResult({ found: false });
      } else {
        showError('Erro ao buscar usuário');
      }
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  // --- Validação de permissões ---
  const handlePermissionChange = (entityKey, newLevel) => {
    setPermissions(prev => {
      const updated = { ...prev, [entityKey]: newLevel };

      // Professional sempre >= Read
      if (entityKey === 'professional') {
        updated.professional = Math.max(newLevel, 1);
      }

      // Appointments=ReadWrite exige Schedules >= Read
      if (entityKey === 'appointments' && newLevel === 2 && updated.schedules === 0) {
        updated.schedules = 1;
      }

      // Se desmarcar Schedules, Appointments=RW cai para R
      if (entityKey === 'schedules' && newLevel === 0 && updated.appointments === 2) {
        updated.appointments = 1;
      }

      return updated;
    });
  };

  // --- Conceder acesso ---
  const handleGrant = async () => {
    if (!searchResult?.found || !searchResult.user) return;

    const permissionsList = [];
    permissionsList.push({ entityType: 1, accessLevel: permissions.professional });
    if (permissions.appointments > 0) permissionsList.push({ entityType: 2, accessLevel: permissions.appointments });
    if (permissions.schedules > 0) permissionsList.push({ entityType: 3, accessLevel: permissions.schedules });
    if (permissions.persons > 0) permissionsList.push({ entityType: 4, accessLevel: permissions.persons });

    setGrantLoading(true);
    try {
      await professionalAccessService.grantAccess(professional.id, {
        userId: searchResult.user.id,
        permissions: permissionsList,
      });
      showSuccess(`Acesso concedido para ${searchResult.user.name}!`);
      setSearchEmail('');
      setSearchResult(null);
      setPermissions({ ...defaultPermissions });
      loadAccessList();
      setActiveTab(1);
      onSuccess?.();
    } catch (err) {
      const msg = err.response?.data?.message || 'Erro ao conceder acesso';
      showError(msg);
    } finally {
      setGrantLoading(false);
    }
  };

  // --- Editar permissões ---
  const handleStartEdit = (accessSummary) => {
    setEditingUserId(accessSummary.grantedToUserId);
    const perms = { professional: 1, appointments: 0, schedules: 0, persons: 0 };
    (accessSummary.permissions || []).forEach(p => {
      const key = entityTypeToKey(p.entityType);
      if (key !== 'unknown') perms[key] = accessLevelToNum(p.accessLevel);
    });
    setEditPermissions(perms);
  };

  const handleEditPermissionChange = (entityKey, newLevel) => {
    setEditPermissions(prev => {
      const updated = { ...prev, [entityKey]: newLevel };
      if (entityKey === 'professional') updated.professional = Math.max(newLevel, 1);
      if (entityKey === 'appointments' && newLevel === 2 && updated.schedules === 0) updated.schedules = 1;
      if (entityKey === 'schedules' && newLevel === 0 && updated.appointments === 2) updated.appointments = 1;
      return updated;
    });
  };

  const handleSaveEdit = async (accessSummary) => {
    const permissionsList = [];
    if (editPermissions.professional > 0) permissionsList.push({ entityType: 1, accessLevel: editPermissions.professional });
    if (editPermissions.appointments > 0) permissionsList.push({ entityType: 2, accessLevel: editPermissions.appointments });
    if (editPermissions.schedules > 0) permissionsList.push({ entityType: 3, accessLevel: editPermissions.schedules });
    if (editPermissions.persons > 0) permissionsList.push({ entityType: 4, accessLevel: editPermissions.persons });

    setEditLoading(true);
    try {
      await professionalAccessService.updateAccess(
        professional.id,
        accessSummary.grantedToUserId,
        { permissions: permissionsList }
      );
      showSuccess('Permissões atualizadas!');
      setEditingUserId(null);
      loadAccessList();
      onSuccess?.();
    } catch (err) {
      showError(err.response?.data?.message || 'Erro ao atualizar permissões');
    } finally {
      setEditLoading(false);
    }
  };

  // --- Revogar acesso ---
  const handleRevoke = async (grantedToUserId) => {
    setRevokeLoading(true);
    try {
      await professionalAccessService.revokeAccess(professional.id, grantedToUserId);
      showSuccess('Acesso revogado!');
      setRevokeConfirm(null);
      loadAccessList();
      onSuccess?.();
    } catch {
      showError('Erro ao revogar acesso');
    } finally {
      setRevokeLoading(false);
    }
  };

  // --- Enviar convite ---
  const handleSendInvite = async () => {
    setInviteLoading(true);
    try {
      await invitesService.create({
        email: searchEmail.trim(),
        context: 'professional_access',
        message: `Convite para colaborar no Agendário com o profissional ${professional?.name}`,
      });
      showSuccess(`Convite enviado para ${searchEmail.trim()}!`);
      setSearchResult(null);
      setSearchEmail('');
      loadInvites();
    } catch (err) {
      const msg = err.response?.data?.message || 'Erro ao enviar convite';
      showError(msg);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await invitesService.cancel(inviteId);
      showSuccess('Convite cancelado');
      loadInvites();
    } catch {
      showError('Erro ao cancelar convite');
    }
  };

  // --- Render de tabela de permissões (reutilizável) ---
  const renderPermissionsTable = (perms, onChange, disabled = false) => (
    <Table size="small" sx={{ mt: 1 }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 'bold' }}>Entidade</TableCell>
          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Nenhum</TableCell>
          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Visualização</TableCell>
          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Edição</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {permissionRows.map(row => (
          <TableRow key={row.key}>
            <TableCell>{row.label}</TableCell>
            <TableCell align="center">
              <Radio
                size="small"
                checked={perms[row.key] === 0}
                onChange={() => onChange(row.key, 0)}
                disabled={disabled || row.disableNone}
              />
            </TableCell>
            <TableCell align="center">
              <Radio
                size="small"
                checked={perms[row.key] === 1}
                onChange={() => onChange(row.key, 1)}
                disabled={disabled}
              />
            </TableCell>
            <TableCell align="center">
              <Radio
                size="small"
                checked={perms[row.key] === 2}
                onChange={() => onChange(row.key, 2)}
                disabled={disabled}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  // --- Render de chip de permissão ---
  const renderPermissionChips = (permsArray) => (
    <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
      {(permsArray || []).map(p => (
        <Chip
          key={p.entityType}
          label={`${entityTypeLabels[p.entityType] || '?'}: ${accessLevelLabels[p.accessLevel] || '?'}`}
          size="small"
          variant="outlined"
          color={p.accessLevel === 2 || p.accessLevel === 'ReadWrite' ? 'primary' : 'default'}
        />
      ))}
    </Box>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <ShareIcon color="primary" />
            <Typography variant="h6" component="span">
              Compartilhar Profissional — {professional?.name}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab label="Compartilhar" />
            <Tab label={`Gerenciar Acessos${accessList.length > 0 ? ` (${accessList.length})` : ''}`} />
          </Tabs>

          {/* === ABA COMPARTILHAR === */}
          {activeTab === 0 && (
            <Box>
              {/* Busca por email */}
              <Typography variant="subtitle2" gutterBottom>
                Buscar usuário por email
              </Typography>
              <Box display="flex" gap={1} alignItems="flex-start">
                <TextField
                  fullWidth
                  size="small"
                  placeholder="email@exemplo.com"
                  value={searchEmail}
                  onChange={(e) => {
                    setSearchEmail(e.target.value);
                    if (searchResult) setSearchResult(null);
                  }}
                  onKeyDown={handleSearchKeyDown}
                  disabled={searchLoading}
                />
                <Button
                  variant="outlined"
                  onClick={handleSearch}
                  disabled={searchLoading || !searchEmail.trim()}
                  startIcon={searchLoading ? <CircularProgress size={16} /> : <PersonSearchIcon />}
                  sx={{ flexShrink: 0 }}
                >
                  Buscar
                </Button>
              </Box>

              {/* Resultado da busca */}
              {searchResult && (
                <Box mt={2}>
                  {searchResult.found ? (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'success.main', fontSize: 14 }}>
                          {searchResult.user.name?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {searchResult.user.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {searchResult.user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </Alert>
                  ) : (
                    <Box>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        Nenhum usuário encontrado com este email. Você pode enviar um convite para criar conta na plataforma.
                      </Alert>
                      <Button
                        variant="outlined"
                        startIcon={inviteLoading ? <CircularProgress size={16} /> : <EmailIcon />}
                        onClick={handleSendInvite}
                        disabled={inviteLoading}
                      >
                        Enviar convite para {searchEmail.trim()}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {/* Seleção de permissões */}
              {searchResult?.found && (
                <Box mt={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Permissões
                  </Typography>
                  {renderPermissionsTable(permissions, handlePermissionChange)}
                  <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                    <Typography variant="caption">
                      <strong>Profissional (Visualização)</strong> é sempre obrigatório. &nbsp;
                      <strong>Agendamentos (Edição)</strong> exige <strong>Agendas (Visualização)</strong> no mínimo.
                    </Typography>
                  </Alert>
                </Box>
              )}

              {/* Convites pendentes */}
              {pendingInvites.length > 0 && (
                <Box mt={3}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Convites pendentes ({pendingInvites.length})
                  </Typography>
                  {pendingInvites.map((invite) => (
                    <Box
                      key={invite.id}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        p: 1.5, mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="body2">{invite.invitedEmail}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Enviado em {new Date(invite.createdAt).toLocaleDateString('pt-BR')}
                        </Typography>
                      </Box>
                      <Tooltip title="Cancelar convite">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleCancelInvite(invite.id)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* === ABA GERENCIAR ACESSOS === */}
          {activeTab === 1 && (
            <Box>
              {accessLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : accessList.length === 0 ? (
                <Alert severity="info">
                  Nenhum acesso compartilhado para este profissional.
                </Alert>
              ) : (
                accessList.map((access) => (
                  <Box
                    key={access.grantedToUserId}
                    sx={{
                      p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1,
                    }}
                  >
                    {/* Header do acesso */}
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14 }}>
                          {access.grantedToUserName?.charAt(0)?.toUpperCase() || '?'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {access.grantedToUserName || 'Usuário'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {access.grantedToUserEmail}
                            {access.grantedAt && (
                              <> · Desde {new Date(access.grantedAt).toLocaleDateString('pt-BR')}</>
                            )}
                          </Typography>
                        </Box>
                      </Box>
                      <Box display="flex" gap={0.5}>
                        {editingUserId !== access.grantedToUserId ? (
                          <>
                            <Tooltip title="Editar permissões">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleStartEdit(access)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {revokeConfirm === access.grantedToUserId ? (
                              <Box display="flex" gap={0.5} alignItems="center">
                                <Typography variant="caption" color="error">Confirmar?</Typography>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRevoke(access.grantedToUserId)}
                                  disabled={revokeLoading}
                                >
                                  {revokeLoading ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => setRevokeConfirm(null)}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            ) : (
                              <Tooltip title="Revogar acesso">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setRevokeConfirm(access.grantedToUserId)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        ) : (
                          <>
                            <Tooltip title="Salvar">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleSaveEdit(access)}
                                disabled={editLoading}
                              >
                                {editLoading ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancelar">
                              <IconButton
                                size="small"
                                onClick={() => setEditingUserId(null)}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </Box>

                    {/* Permissões */}
                    {editingUserId === access.grantedToUserId ? (
                      renderPermissionsTable(editPermissions, handleEditPermissionChange)
                    ) : (
                      renderPermissionChips(access.permissions)
                    )}
                  </Box>
                ))
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose}>
            Fechar
          </Button>
          {activeTab === 0 && searchResult?.found && (
            <Button
              variant="contained"
              onClick={handleGrant}
              disabled={grantLoading}
              startIcon={grantLoading ? <CircularProgress size={16} /> : <ShareIcon />}
            >
              Compartilhar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar próprio do dialog */}
      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </>
  );
}
